import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { useAuth } from './useAuth';
import { toast } from '@/hooks/use-toast';

type LocationRow = Database['public']['Tables']['charging_locations']['Row'];

export interface ChargingLocation extends LocationRow {
  // Additional typed fields for charging portal
}

export function useChargingLocations() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: locations = [], isLoading, error } = useQuery({
    queryKey: ['charging-locations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('charging_locations')
        .select('*')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });

  // Realtime subscription voor live updates
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('charging-locations-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'charging_locations',
        },
        (payload) => {
          console.log('Realtime location update:', payload);
          
          // Invalideer de query om data opnieuw op te halen
          queryClient.invalidateQueries({ queryKey: ['charging-locations'] });
          
          // Toon notificatie bij relevante events
          if (payload.eventType === 'DELETE' || 
              (payload.eventType === 'UPDATE' && payload.new && (payload.new as any).deleted_at)) {
            toast({
              title: 'Locatie verwijderd',
              description: 'Lijst wordt automatisch bijgewerkt',
            });
          } else if (payload.eventType === 'INSERT') {
            toast({
              title: 'Nieuwe locatie toegevoegd',
              description: 'Lijst wordt automatisch bijgewerkt',
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  const geocodeAddress = async (address: string, city?: string): Promise<{ lat: number; lng: number } | null> => {
    try {
      // Use city as fallback if no address provided
      const query = address || city;
      if (!query) return null;

      const fullQuery = `${query}, Netherlands`;
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(fullQuery)}&format=json&countrycodes=nl&limit=1`,
        {
          headers: {
            'User-Agent': 'Greenvision Charging Portal',
          },
        }
      );
      
      const data = await response.json();
      if (data && data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
        };
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  const updateLocationCoords = useMutation({
    mutationFn: async ({ locationId, latitude, longitude }: { locationId: string; latitude: number; longitude: number }) => {
      const { error } = await supabase
        .from('charging_locations')
        .update({ 
          latitude: latitude.toString(), 
          longitude: longitude.toString() 
        } as any)
        .eq('id', locationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['charging-locations'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Fout bij opslaan coördinaten',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const autoGeocodeLocations = async () => {
    // Filter locations without coords but with city
    const locationsToGeocode = locations.filter(
      (l) => !l.latitude && !l.longitude && l.city
    );

    let successCount = 0;
    for (const location of locationsToGeocode) {
      const coords = await geocodeAddress(location.address || '', location.city);
      if (coords) {
        await updateLocationCoords.mutateAsync({
          locationId: location.id,
          latitude: coords.lat,
          longitude: coords.lng,
        });
        successCount++;
      }
    }

    if (successCount > 0) {
      toast({
        title: 'Geocoding voltooid',
        description: `${successCount} van ${locationsToGeocode.length} locaties gecodeerd`,
      });
    }
  };

  return {
    locations,
    isLoading,
    error,
    autoGeocodeLocations,
    updateLocationCoords: updateLocationCoords.mutateAsync,
    geocodeAddress,
  };
}
